import React, { useState, useEffect, useContext, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import isEmpty from "lodash/isEmpty";
import { FormikProps, withFormik } from "formik";
import moment from "moment";

import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import { reportService } from "@/shared/services/reportService";
import { renderTemplate as renderTemplateBase } from "@/shared/helpers/reportHelper";
import NoDataFound from "@/components/NoDataFound";
import CampaignReportFiltersBase from "../components/CampaignReportFilters";
import ProgressChartBase from "@/components/charts/ProgressChart";
import TableChartBase from "@/components/charts/TableChart";
import AreaChartBase from "@/components/charts/AreaChart";
import DoughnutChartBase from "@/components/charts/DoughnutChart";
import TotalSpendChartBase from "@/components/charts/TotalSpendChart";
import BarChartBase from "@/components/charts/BarChart";
import PageLayoutBase from "@/components/layouts/page/PageLayout";
import BackLink from "@/components/BackLink";
import PageHeaderBase from "@/components/layouts/pageHeaders/PageHeader";
import useQuery from "@/helpers/hooks/useQuery";
import CampaignReportDetailBase from "../components/CampaignReportDetail";
import useFetcher from "@/helpers/hooks/useFetcher";
import { CUSTOM_WIDGET_PARAM } from "@/config/constants";
import StackedBarChartBase from "@/components/charts/StackedBarChart";
import { campaignReportHelper } from "@/features/campaigns/shared/helpers/campaignReportHelper";
import {
  formatDate,
  isBrandHasDrillFeature,
  parseDateString,
} from "@/features/campaigns/shared/helpers/drillUpDownHelper";
import { Button } from "@mui/material";
import { getDashboardPath, getWidgetHiddenKeys } from "@/helpers/utils";
// import { parseDateString, formatDate } from "@/features/campaigns/shared/helpers/drillUpDownHelper";

type DateRangePart = {
  year: string | number;
  month: string | number;
  date?: string | number;
};

type CustomMonthRange = {
  from: DateRangePart;
  to: DateRangePart;
};

type CampaignReportFormValues = {
  start_date: string;
  end_date: string;
  location_id: string;
  channel_id: string;
  group_id: string;
  filter: string;
  customRange: "" | string[] | CustomMonthRange;
};

type ParticipantData = {
  id?: string | number;
  system_generated_participant_id?: string;
};

type LoggedInUser = {
  id?: string | number;
  fk_role_id?: string | number;
  participantData?: ParticipantData;
};

type SelectedBrand = {
  id?: string | number;
  system_generated_brand_id?: string;
};

type CampaignReportTemplateColumn = {
  widget?: CampaignReportWidget;
};

type CampaignReportTemplateRow = {
  columnRows?: Array<{
    columns?: CampaignReportTemplateColumn[];
  }>;
};

type CampaignReportResponse = {
  campaign_name?: string;
  template?: {
    rows?: CampaignReportTemplateRow[];
  };
  reportingPeriod?: {
    from: DateRangePart;
    to: DateRangePart;
  };
};

type CampaignReportWidget = {
  id?: string | number;
  type?: string;
  chart_type?: string;
  channel?: string;
  name?: string;
  alt_name?: string;
  data?: Array<Record<string, unknown>> | unknown;
  format?: unknown;
};

type ReportRenderable = React.ComponentType<any>;

const ProgressChart = ProgressChartBase as ReportRenderable;
const TableChart = TableChartBase as ReportRenderable;
const AreaChart = AreaChartBase as ReportRenderable;
const DoughnutChart = DoughnutChartBase as ReportRenderable;
const TotalSpendChart = TotalSpendChartBase as ReportRenderable;
const BarChart = BarChartBase as ReportRenderable;
const StackedBarChart = StackedBarChartBase as ReportRenderable;
const PageLayout = PageLayoutBase as ReportRenderable;
const PageHeader = PageHeaderBase as ReportRenderable;
const CampaignReportDetail = CampaignReportDetailBase as ReportRenderable;
const CampaignReportFilters = CampaignReportFiltersBase as ReportRenderable;
const renderTemplate = renderTemplateBase as (_args: any) => React.ReactNode;

const getMonthRange = (
  customRange: CampaignReportFormValues["customRange"]
): CustomMonthRange | null =>
  customRange && !Array.isArray(customRange) && typeof customRange === "object"
    ? customRange
    : null;

const getDateRange = (
  customRange: CampaignReportFormValues["customRange"]
): string[] | null => (Array.isArray(customRange) ? customRange : null);

const CampaignReport = ({
  values,
  errors,
  handleChange,
  setFieldValue,
}: FormikProps<CampaignReportFormValues>) => {
  const { campaignSysId } = useParams();
  const { locationID } = useQuery();

  const { selectedBrand, loggedInUser } = useContext(GlobalContext) as {
    selectedBrand?: SelectedBrand | null;
    loggedInUser: LoggedInUser;
  };
  const loader = useContext(LoaderContext);

  const [campaignReport, setCampaignReport] =
    useState<CampaignReportResponse | null>(null);

  const reqData = useMemo(() => {
    const monthRange = getMonthRange(values.customRange);
    const dateRange = getDateRange(values.customRange);
    const endPeriod = moment(
      `${monthRange?.to?.year} - ${monthRange?.to?.month}`,
      "YYYY-MM"
    );
    return {
      system_generated_campaign_id: `${campaignSysId}`,
      fk_brand_id: selectedBrand?.id,
      fk_participant_id: loggedInUser?.participantData
        ? loggedInUser?.participantData?.id
        : 0,
      participant_system_id: loggedInUser?.participantData
        ? loggedInUser?.participantData?.system_generated_participant_id
        : "",
      userId: loggedInUser.id,
      fk_role_id: loggedInUser.fk_role_id,
      group_id: values.group_id ? values.group_id : "",
      location_id: values.location_id ? values.location_id : "",
      channel_id: values.channel_id ? values.channel_id : "",
      filter: values.filter,
      // start_period:
      //   values.filter === "custom" && values.customRange
      //     ? formatDate(parseDateString(values.customRange[0]))
      //     : "",
      // end_period:
      //   values.filter === "custom" && values.customRange
      //     ? formatDate(parseDateString(values.customRange[1]))
      //     : "",
      start_period:
        values.filter === "custom" && values.customRange
          ? isBrandHasDrillFeature(selectedBrand?.system_generated_brand_id)
            ? formatDate(parseDateString(dateRange?.[0]))
            : `${monthRange?.from.year}-${monthRange?.from.month}-01`
          : "",
      end_period:
        values.filter === "custom" && values.customRange
          ? isBrandHasDrillFeature(selectedBrand?.system_generated_brand_id)
            ? formatDate(parseDateString(dateRange?.[1]))
            : `${
                endPeriod && endPeriod.isValid()
                  ? endPeriod.endOf("month").format("YYYY-MM-DD")
                  : ""
              }`
          : "",
    };
  }, [
    selectedBrand?.id,
    selectedBrand?.system_generated_brand_id,
    loggedInUser,
    campaignSysId,
    values,
  ]);

  const {
    data: reportDetail,
    isLoading: loadingDetail,
    isValidating: isValidatingDetail,
  } = useFetcher<CampaignReportResponse>(
    Object.values(errors).length === 0 ||
      Object.values(errors).every((v) => v === "")
      ? { ...reqData, detail: true }
      : null,
    reportService.getCampaignReportDetail,
    { revalidateOnFocus: false }
  );

  const {
    data: report,
    isLoading: loadingReport,
    isValidating: isValidatingReport,
  } = useFetcher<CampaignReportResponse>(
    Object.values(errors).length === 0 ||
      Object.values(errors).every((v) => v === "")
      ? reqData
      : null,
    reportService.getCampaignReport,
    { revalidateOnFocus: false }
  );

  const loading = loadingDetail || loadingReport;
  const isValidating = isValidatingDetail || isValidatingReport;

  const initialRange = useMemo(() => {
    return campaignReportHelper.getInitialCustomDateRange(reportDetail);
  }, [reportDetail]);

  useEffect(() => {
    setFieldValue("location_id", "");
  }, [values.group_id, setFieldValue]);

  useEffect(() => {
    if (values.filter !== "custom") {
      setFieldValue("customRange", "");
    }
  }, [values.filter, setFieldValue]);

  useEffect(() => {
    loader(loading || isValidating);
  }, [loading, loader, isValidating]);

  useEffect(() => {
    if (locationID) {
      setFieldValue("location_id", locationID);
    }
  }, [locationID, setFieldValue]);

  useEffect(() => {
    if (!loading) {
      setCampaignReport(reportDetail);
    }
  }, [loading, reportDetail]);

  useEffect(() => {
    //when no widget added for previously selected channel
    if (report && values?.channel_id) {
      const template = report?.template;
      const channelAvailable = template?.rows.some((row) =>
        row.columnRows.some((colRow) =>
          colRow.columns.some(
            (col) => col.widget && col.widget.channel === values.channel_id
          )
        )
      );

      if (!channelAvailable) {
        setFieldValue("channel_id", "");
      }
    }
  }, [report, values?.channel_id, setFieldValue]);

  const reportData = !loading && !isEmpty(report) ? report : campaignReport; //To set previous data until fetching new filtered data

  if ((!reportData || isEmpty(reportData)) && !loading) {
    return (
      <NoDataFound
        message={
          <div>
            <div>No Data found!</div>
            <Button
              component={Link}
              to={`${getDashboardPath()}`}
              variant="contained"
              color="primary"
              className="my-4"
              size="large"
            >
              Back to dashboard
            </Button>
          </div>
        }
      />
    );
  }

  const renderChartWidget = (widget: CampaignReportWidget) => {
    if (
      !widget.data ||
      (Array.isArray(widget.data) && widget.data.length === 0)
    ) {
      return <NoDataFound message="There is no record" height="100%" />;
    }

    let chartToRender: React.ReactNode = null;

    if (!Array.isArray(widget.data)) {
      return chartToRender;
    }

    const labels = Object.keys(widget.data[0]);

    if (!labels || labels.length === 0) {
      return chartToRender;
    }

    if (widget.chart_type === "progress") {
      const max = widget.data[0][labels[1]];
      chartToRender = (
        <ProgressChart
          data={widget.data}
          labels={labels}
          max={max ? max : 100}
          formats={widget?.format}
        />
      );
    } else if (widget.chart_type === "area") {
      chartToRender = (
        <AreaChart
          data={widget.data}
          labels={labels}
          title={widget.alt_name || widget.name}
          formats={widget?.format}
          widgetId={widget.id}
          values={values}
          initialRange={initialRange}
        />
      );
    } else if (widget.chart_type === "doughnut") {
      chartToRender = (
        <DoughnutChart
          data={widget.data}
          labels={labels}
          title={widget.alt_name || widget.name}
          formats={widget?.format}
        />
      );
    } else if (widget.chart_type === "bar") {
      chartToRender = (
        <BarChart
          data={widget.data}
          labels={labels}
          title={widget.alt_name || widget.name}
          formats={widget?.format}
        />
      );
    } else if (widget.chart_type === "stacked_bar") {
      chartToRender = (
        <StackedBarChart
          data={widget.data}
          title={widget.alt_name || widget.name}
          formats={widget?.format}
        />
      );
    } else if (widget.chart_type === "total_spend") {
      chartToRender = (
        <TotalSpendChart
          data={widget.data}
          widgetId={widget.id}
          labels={labels}
          title={widget.alt_name || widget.name}
          formats={widget?.format}
          showTotal
          values={values}
          initialRange={initialRange}
        />
      );
    }

    return chartToRender;
  };

  const renderTable = (widget: CampaignReportWidget) => {
    if (
      !widget.data ||
      (Array.isArray(widget.data) && widget.data.length === 0)
    ) {
      return <NoDataFound message="There is no record" height="100%" />;
    }

    if (Array.isArray(widget.data)) {
      const headings = Object.keys(widget.data[0]);
      const hiddenKeys = getWidgetHiddenKeys(widget, ["creative_image"]);
      return (
        <TableChart
          data={widget.data}
          headings={headings}
          formats={widget?.format}
          hiddenKeys={hiddenKeys}
        />
      );
    }

    return null;
  };

  return (
    <>
      <PageLayout className="campaign-report-container mx-0 mx-sm-2">
        <BackLink />
        <PageHeader title="Digital Campaign Report" showLastUpdate />
        <div id="campaignReport" className="report campaign-report mt-0 mb-5">
          <div className="card campaign-detail">
            <div className="card-body">
              <CampaignReportDetail campaign={campaignReport} values={values} />
            </div>
          </div>

          <CampaignReportFilters
            handleChange={handleChange}
            values={values}
            campaign={campaignReport}
            initialRange={initialRange}
            setFieldValue={setFieldValue}
          />
          {renderTemplate({
            isLoading: loading,
            report,
            renderTable,
            renderChartWidget,
            channel_name: values.channel_id,
            reportType: "campaign",
            embedWidgetParams: {
              [CUSTOM_WIDGET_PARAM.BrandID]:
                selectedBrand?.system_generated_brand_id,
              [CUSTOM_WIDGET_PARAM.CampaignID]: campaignSysId,
            },
            values,
          })}
        </div>
      </PageLayout>
    </>
  );
};

export default withFormik({
  mapPropsToValues: () => {
    return {
      start_date: "",
      end_date: "",
      location_id: "",
      channel_id: "",
      group_id: "",
      filter: "custom",
      customRange: "",
    };
  },
  enableReinitialize: true,
  handleSubmit: () => {},
})(CampaignReport);
